import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, RefreshCw, AlertCircle, CheckCircle2, ArrowUpRight, ArrowDownLeft, History } from 'lucide-react';
import { Account, Transaction } from '../types';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { recalculateAccountBalance } from '../services/financeService';
import { useFirebase } from './FirebaseProvider';
import { listUserRows, updateRow } from '../lib/db';

interface AccountAuditProps {
  account: Account;
  onClose: () => void;
}

export const AccountAudit: React.FC<AccountAuditProps> = ({ account, onClose }) => {
  const { user, effectiveUserId } = useFirebase();
  const [allTransactions, setAllTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [editingInitial, setEditingInitial] = useState(false);
  const [newInitial, setNewInitial] = useState(account.initialBalance?.toString() || '0');

  useEffect(() => {
    if (!effectiveUserId) return;
    const loadTransactions = async () => {
      setLoading(true);
      try {
        const txs = await listUserRows<Transaction>('transactions', effectiveUserId);
        setAllTransactions(txs);
      } catch (error) {
        console.error('Error loading transactions for audit:', error);
      } finally {
        setLoading(false);
      }
    };
    loadTransactions();
  }, [effectiveUserId]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(amount);
  };

  const handleUpdateInitial = async () => {
    if (!effectiveUserId) return;
    const val = parseFloat(newInitial);
    if (isNaN(val)) return;
    
    try {
      // Calculate the new final balance based on the new initial balance
      const totalChange = allTransactions.reduce((sum, tx) => {
        const accountDetails = tx.details.filter(d => d.accountId === account.id);
        const amount = accountDetails.reduce((s, d) => s + (d.type === 'CREDIT' ? d.amount : -d.amount), 0);
        return sum + amount;
      }, 0);
      const newBalance = val + totalChange;

      await updateRow('accounts', account.id, {
        initialBalance: val,
        balance: newBalance
      });
      setEditingInitial(false);
    } catch (error) {
      console.error("Error updating initial balance:", error);
    }
  };

  const accountMovements = useMemo(() => {
    // Filter transactions for this account and sort by date ascending for running balance
    const filtered = allTransactions
      .filter(tx => tx.details.some(d => d.accountId === account.id))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    let runningBalance = account.initialBalance || 0;
    return filtered.map(tx => {
      // Sum all details for this account in this transaction
      const accountDetails = tx.details.filter(d => d.accountId === account.id);
      const amount = accountDetails.reduce((sum, d) => sum + (d.type === 'CREDIT' ? d.amount : -d.amount), 0);
      runningBalance += amount;
      return {
        ...tx,
        amount,
        runningBalance
      };
    }).reverse(); // Reverse for display (newest first)
  }, [account, allTransactions]);

  const calculatedFinalBalance = useMemo(() => {
    const totalChange = allTransactions.reduce((sum, tx) => {
      const accountDetails = tx.details.filter(d => d.accountId === account.id);
      const amount = accountDetails.reduce((s, d) => s + (d.type === 'CREDIT' ? d.amount : -d.amount), 0);
      return sum + amount;
    }, 0);
    return (account.initialBalance || 0) + totalChange;
  }, [account, allTransactions]);

  const hasDifference = Math.abs(calculatedFinalBalance - account.balance) > 0.01;

  const handleRecalculate = async () => {
    if (!effectiveUserId) return;
    setRecalculating(true);
    try {
      await recalculateAccountBalance(effectiveUserId, account.id);
    } catch (error) {
      console.error("Error recalculating balance:", error);
    } finally {
      setRecalculating(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-zinc-900 border border-zinc-800 w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh]"
      >
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between bg-zinc-800/30">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-500/10 rounded-xl border border-indigo-500/20">
              <History className="text-indigo-400" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-zinc-100">Auditoría: {account.name}</h2>
              <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium">Historial de saldos y movimientos</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-full transition-colors">
            <X size={20} />
          </button>
        </div>

        {loading ? (
          <div className="p-12 flex items-center justify-center text-zinc-500">
            <RefreshCw size={20} className="animate-spin mr-3" />
            Cargando historial completo...
          </div>
        ) : (
          <>
            <div className="p-6 bg-zinc-950/50 border-b border-zinc-800 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <div className="text-[10px] text-zinc-500 uppercase font-bold tracking-widest flex items-center justify-between">
                  Saldo Inicial
                  <button 
                    onClick={() => {
                      if (!editingInitial) {
                        setNewInitial(account.initialBalance?.toString() || '0');
                      }
                      setEditingInitial(!editingInitial);
                    }}
                    className="text-[9px] text-indigo-400 hover:underline"
                  >
                    {editingInitial ? 'Cancelar' : 'Editar'}
                  </button>
                </div>
                {editingInitial ? (
                  <div className="flex items-center gap-2">
                    <input 
                      type="number"
                      value={newInitial}
                      onChange={(e) => setNewInitial(e.target.value)}
                      className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-sm w-full focus:outline-none focus:border-indigo-500"
                    />
                    <button 
                      onClick={handleUpdateInitial}
                      className="p-1 bg-indigo-500 rounded-lg hover:bg-indigo-600 transition-colors"
                    >
                      <CheckCircle2 size={14} className="text-black" />
                    </button>
                  </div>
                ) : (
                  <div className="text-lg font-bold text-zinc-300">{formatCurrency(account.initialBalance || 0)}</div>
                )}
              </div>
              <div className="space-y-1">
                <div className="text-[10px] text-zinc-500 uppercase font-bold tracking-widest">Saldo Calculado</div>
                <div className="text-lg font-bold text-zinc-100">{formatCurrency(calculatedFinalBalance)}</div>
              </div>
              <div className="space-y-1">
                <div className="text-[10px] text-zinc-500 uppercase font-bold tracking-widest">Saldo Actual</div>
                <div className={`text-lg font-bold ${hasDifference ? 'text-red-500' : 'text-emerald-500'}`}>
                  {formatCurrency(account.balance)}
                </div>
              </div>
            </div>

            {hasDifference && (
              <div className="px-6 py-4 bg-red-500/10 border-b border-red-500/20 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 text-red-400 text-sm">
                  <AlertCircle size={18} />
                  <span>Se detectó una diferencia entre el historial y el saldo actual.</span>
                </div>
                <button
                  onClick={handleRecalculate}
                  disabled={recalculating}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-black font-bold rounded-xl text-xs transition-all disabled:opacity-50"
                >
                  <RefreshCw size={14} className={recalculating ? 'animate-spin' : ''} />
                  Recalcular Saldo
                </button>
              </div>
            )}

            {!hasDifference && (
              <div className="px-6 py-4 bg-emerald-500/10 border-b border-emerald-500/20 flex items-center gap-3 text-emerald-400 text-sm">
                <CheckCircle2 size={18} />
                <span>El saldo está sincronizado con el historial de movimientos.</span>
              </div>
            )}

            <div className="flex-1 overflow-y-auto custom-scrollbar">
              <div className="divide-y divide-zinc-800">
                {accountMovements.length === 0 ? (
                  <div className="p-12 text-center text-zinc-500">No hay movimientos registrados para esta cuenta.</div>
                ) : (
                  accountMovements.map((tx, idx) => (
                    <div key={tx.id} className="p-4 flex items-center justify-between hover:bg-zinc-800/30 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${
                          tx.amount > 0 ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500' : 'bg-red-500/10 border-red-500/20 text-red-500'
                        }`}>
                          {tx.amount > 0 ? <ArrowUpRight size={16} /> : <ArrowDownLeft size={16} />}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-zinc-200">{tx.description}</div>
                          <div className="text-[10px] text-zinc-500">
                            {format(new Date(tx.date), "d 'de' MMMM, yyyy", { locale: es })}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-sm font-bold ${tx.amount > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                          {tx.amount > 0 ? '+' : ''}{formatCurrency(tx.amount)}
                        </div>
                        <div className="text-[10px] text-zinc-500 font-mono">
                          Saldo: {formatCurrency(tx.runningBalance)}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="p-4 bg-zinc-800/30 border-t border-zinc-800 text-center">
              <p className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold">Fin del Historial</p>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};
